import React from 'react';
import './Badge.css';

export type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps {
  /** Badge content - text or number */
  children: React.ReactNode;
  /** Visual variant */
  variant?: BadgeVariant;
  /** Size of the badge */
  size?: BadgeSize;
  /** Outlined style (transparent background with border) */
  outlined?: boolean;
  /** Pill shape (fully rounded) */
  pill?: boolean;
  /** Icon to display before the text */
  icon?: React.ReactNode;
  /** Custom CSS class */
  className?: string;
  /** Accessible label for screen readers (overrides visible content) */
  'aria-label'?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  outlined = false,
  pill = false,
  icon,
  className = '',
  'aria-label': ariaLabel,
}) => {
  const classes = [
    'hi-ui-badge',
    `hi-ui-badge--${variant}`,
    `hi-ui-badge--${size}`,
    outlined ? 'hi-ui-badge--outlined' : '',
    pill ? 'hi-ui-badge--pill' : '',
    icon ? 'hi-ui-badge--with-icon' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} aria-label={ariaLabel}>
      {icon && (
        <span className="hi-ui-badge__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="hi-ui-badge__text">{children}</span>
    </span>
  );
};
